import { useCallback, useMemo } from "react";
import datetime from "config/datetime";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useLocalStorage from "hooks/useLocalStorage";
import useSubscription from "hooks/useSubscription";
import { SubscriptionInterval, SubscriptionStatus } from "types/organisation";

export const UPCOMING_RENEWAL_WINDOW_IN_DAYS = 30;
export const NON_DISMISSIBLE_RENEWAL_WINDOW_IN_DAYS = 7;

function useUpcomingRenewalBanner() {
  const subscription = useSubscription();
  const [dismissedRenewalDate, setDismissedRenewalDate] = useLocalStorage<string | null>(
    LocalStorageHelper.keys.upcomingRenewalBannerDismissal,
    null
  );

  const { renewalDate, renewInDays } = useMemo(() => {
    const renewal = datetime(subscription?.currentPeriodEndDate);

    return {
      renewalDate: renewal.format("YYYY-MM-DD"),
      renewInDays: renewal.startOf("day").diff(datetime().startOf("day"), "day"),
    };
  }, [subscription?.currentPeriodEndDate]);

  const isRenewingSoon = useMemo(() => {
    if (!subscription) return false;
    if (
      subscription.status !== SubscriptionStatus.Active ||
      subscription.intervalType !== SubscriptionInterval.Yearly
    ) {
      return false;
    }
    return renewInDays >= 0 && renewInDays <= UPCOMING_RENEWAL_WINDOW_IN_DAYS;
  }, [renewInDays, subscription]);

  const isDismissible = useMemo(() => {
    return isRenewingSoon && renewInDays > NON_DISMISSIBLE_RENEWAL_WINDOW_IN_DAYS;
  }, [isRenewingSoon, renewInDays]);

  const isEligible = useMemo(() => {
    const isDismissed = isDismissible && dismissedRenewalDate === renewalDate;
    return isRenewingSoon && !isDismissed;
  }, [dismissedRenewalDate, isDismissible, isRenewingSoon, renewalDate]);

  const dismiss = useCallback(() => {
    if (!isDismissible) return;
    setDismissedRenewalDate(renewalDate);
  }, [isDismissible, renewalDate, setDismissedRenewalDate]);

  return { isEligible, isDismissible, dismiss };
}

export default useUpcomingRenewalBanner;
